/**
 * WebCodecs configuration helpers: pick an H.264 level that actually covers the
 * capture, negotiate an audio codec the browser can encode, and derive sane
 * bitrates. Everything is validated with `isConfigSupported` before use.
 */

export type Quality = 'balanced' | 'high' | 'max';

export function evenDim(value: number): number {
  return Math.max(2, Math.floor(value / 2) * 2);
}

interface Level {
  /** hex suffix of the AVC level_idc, e.g. 0x28 is level 4.0 */
  level: number;
  maxMacroblocks: number;
  maxMacroblocksPerSecond: number;
}

// H.264 level limits (Annex A, tables A-1/A-2).
const LEVELS: Level[] = [
  { level: 0x1f, maxMacroblocks: 3600, maxMacroblocksPerSecond: 108_000 }, // 3.1
  { level: 0x28, maxMacroblocks: 8192, maxMacroblocksPerSecond: 245_760 }, // 4.0
  { level: 0x2a, maxMacroblocks: 8704, maxMacroblocksPerSecond: 522_240 }, // 4.2
  { level: 0x32, maxMacroblocks: 22_080, maxMacroblocksPerSecond: 589_824 }, // 5.0
  { level: 0x33, maxMacroblocks: 36_864, maxMacroblocksPerSecond: 983_040 }, // 5.1
  { level: 0x34, maxMacroblocks: 36_864, maxMacroblocksPerSecond: 2_073_600 }, // 5.2
  { level: 0x3c, maxMacroblocks: 139_264, maxMacroblocksPerSecond: 4_177_920 }, // 6.0
];

export function avcCodecCandidates(width: number, height: number, fps: number): string[] {
  const macroblocks = Math.ceil(width / 16) * Math.ceil(height / 16);
  const perSecond = macroblocks * fps;
  const minimum = LEVELS.find(
    (lvl) => macroblocks <= lvl.maxMacroblocks && perSecond <= lvl.maxMacroblocksPerSecond,
  );
  const levelByte = minimum ?? LEVELS[LEVELS.length - 1];
  const ordered = LEVELS.filter((lvl) => lvl.level >= levelByte.level);

  // Prefer High profile (better quality per bit for screen content), then Main,
  // then Baseline. Screen recordings are text-heavy, so High is usually right.
  const profiles = [
    (level: number) => `avc1.6400${level.toString(16).padStart(2, '0')}`, // High
    (level: number) => `avc1.4d00${level.toString(16).padStart(2, '0')}`, // Main
    (level: number) => `avc1.42e0${level.toString(16).padStart(2, '0')}`, // Baseline (constrained)
  ];

  const candidates: string[] = [];
  for (const profile of profiles) {
    for (const lvl of ordered) candidates.push(profile(lvl.level));
  }
  return candidates;
}

const encoderSupportCache = new Map<string, Promise<boolean>>();

function supportsVideo(config: VideoEncoderConfig): Promise<boolean> {
  const key = JSON.stringify(config);
  const cached = encoderSupportCache.get(key);
  if (cached) return cached;
  const probe = (async () => {
    if (typeof VideoEncoder === 'undefined') return false;
    try {
      const support = await VideoEncoder.isConfigSupported(config);
      return Boolean(support.supported);
    } catch {
      return false;
    }
  })();
  encoderSupportCache.set(key, probe);
  return probe;
}

export interface VideoFormat {
  width: number;
  height: number;
  fps: number;
  bitrate: number;
  codec: string;
}

export function bitrateFor(width: number, height: number, fps: number, quality: Quality): number {
  // ~0.07 bits per pixel per frame lands near 4.5 Mbps at 1080p30, which is a
  // good default for screen content with text.
  const base = width * height * fps * 0.07;
  const multiplier = quality === 'max' ? 2.6 : quality === 'high' ? 1.6 : 1;
  return Math.round(Math.min(Math.max(base * multiplier, 1_500_000), 120_000_000));
}

export async function negotiateVideoFormat(
  width: number,
  height: number,
  fps: number,
  quality: Quality,
  bitrateOverride?: number,
): Promise<VideoFormat> {
  const bitrate = bitrateOverride ?? bitrateFor(width, height, fps, quality);
  const candidates = avcCodecCandidates(width, height, fps);
  for (const codec of candidates) {
    const config: VideoEncoderConfig = {
      codec,
      width,
      height,
      bitrate,
      framerate: fps,
      latencyMode: 'realtime',
      hardwareAcceleration: 'no-preference',
      avc: { format: 'avc' },
    };
    if (await supportsVideo(config)) {
      return { width, height, fps, bitrate, codec };
    }
  }
  throw new Error(
    'This browser cannot encode H.264 at the requested resolution. Try a lower resolution or a Chromium-based browser.',
  );
}

export interface AudioFormat {
  codec: string;
  containerCodec: 'aac' | 'opus';
  sampleRate: number;
  numberOfChannels: number;
  bitrate: number;
}

export async function negotiateAudioFormat(
  sampleRate = 48_000,
  numberOfChannels = 2,
  bitrate = 160_000,
): Promise<AudioFormat | null> {
  if (typeof AudioEncoder === 'undefined') return null;
  const attempts: Array<{ codec: string; containerCodec: 'aac' | 'opus' }> = [
    { codec: 'mp4a.40.2', containerCodec: 'aac' },
    { codec: 'opus', containerCodec: 'opus' },
  ];
  for (const attempt of attempts) {
    try {
      const support = await AudioEncoder.isConfigSupported({
        codec: attempt.codec,
        sampleRate,
        numberOfChannels,
        bitrate,
      });
      if (support.supported) {
        return { ...attempt, sampleRate, numberOfChannels, bitrate };
      }
    } catch {
      // try the next candidate
    }
  }
  return null;
}

export interface CapabilityReport {
  supported: boolean;
  reason: string | null;
  hasTrackProcessor: boolean;
  hasDisplayCapture: boolean;
  video: VideoFormat | null;
  audio: AudioFormat | null;
}

export async function probeCapabilities(): Promise<CapabilityReport> {
  const hasDisplayCapture =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  const hasCodecs = typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';
  const hasTrackProcessor = typeof (globalThis as { MediaStreamTrackProcessor?: unknown }).MediaStreamTrackProcessor === 'function';

  if (!hasDisplayCapture) {
    return {
      supported: false,
      reason: 'Screen capture is unavailable here. Open this page over HTTPS or from localhost.',
      hasTrackProcessor,
      hasDisplayCapture,
      video: null,
      audio: null,
    };
  }
  if (!hasCodecs) {
    return {
      supported: false,
      reason: 'WebCodecs is unavailable in this browser. Recording needs Chrome, Edge, Safari 16.4+ or Firefox 130+.',
      hasTrackProcessor,
      hasDisplayCapture,
      video: null,
      audio: null,
    };
  }

  let video: VideoFormat | null = null;
  try {
    video = await negotiateVideoFormat(1920, 1080, 30, 'balanced');
  } catch {
    video = null;
  }
  const audio = await negotiateAudioFormat();

  if (!video) {
    return {
      supported: false,
      reason: 'No H.264 encoder is available in this browser, so exports cannot be produced.',
      hasTrackProcessor,
      hasDisplayCapture,
      video,
      audio,
    };
  }
  return { supported: true, reason: null, hasTrackProcessor, hasDisplayCapture, video, audio };
}
